import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { Button, ListItem, ListItemButton, ListItemText } from "@mui/material";
import { Box } from "@mui/system";
import { Label12Regular, Label16SemiBold, LazyItem, ScorecardTitle, Drawer, DefaultSearchBar } from "components";
import { DoubleArrowLeft } from "assets";
import { getFromLocalStorage, setToLocalStorage } from "utils";
import { SELECTED_SITE_LOCAL_STORAGE_KEY } from "../../consts";
import { ViewMoreInfoButton } from "../buttons";

interface Site {
    id: number;
    name: string | null;
}

interface SiteSelectionPanelProps {
    sitesData?: any;
}

export const SiteSelectionPanel = ({ sitesData: externalSitesData }: SiteSelectionPanelProps) => {
    const sitesData = useMemo(() =>
        externalSitesData?.sites
            ?.map(({ siteId, siteName }: any) => ({ id: siteId, name: siteName }))
            ?.filter((site: Site) => site.name && site.name.trim() !== '') || [],
        [externalSitesData]
    );

    const [sitesModalOpen, setSitesModalOpen] = useState<boolean>(false);
    const [selectedSite, setSelectedSite] = useState<Site | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");

    const filteredSites = useMemo(() => {
        if (!searchQuery.trim()) {
            return sitesData;
        }
        return sitesData.filter((site: Site) =>
            site.name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [sitesData, searchQuery]);

    const onSiteButtonClick = (site: Site) => {
        setSelectedSite(site);
        setToLocalStorage(SELECTED_SITE_LOCAL_STORAGE_KEY, site);
        setSitesModalOpen(false)
    }

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    }

    const handleModalToggle = (isOpen: boolean) => {
        setSitesModalOpen(isOpen);
        if (!isOpen) {
            setSearchQuery("");
        }
    }

    useEffect(() => {
        if (sitesData.length > 0) {
            const stored = getFromLocalStorage(SELECTED_SITE_LOCAL_STORAGE_KEY);
            const validStoredSite = stored && sitesData.find((site: Site) => site.id === stored.id);
            if (validStoredSite) {
                setSelectedSite(validStoredSite);
            } else {
                const defaultSite = sitesData[0];
                setSelectedSite(defaultSite);
                setToLocalStorage(SELECTED_SITE_LOCAL_STORAGE_KEY, defaultSite);
            }
        }
    }, [sitesData]);

    const defaultButtonStyle: CSSProperties = {
        position: "absolute",
        top: "0",
        right: sitesModalOpen ? "418px" : "0px",
        zIndex: 1400,
        transition: "right 0.1s ease-in-out",
        borderRadius: "20px 0 0 20px",
        border: '1px solid #054254',
        borderRight: "0px",
        background: 'white',
        width: '180px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    return (
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignContent: 'center', position: 'relative', marginRight: '-24px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ScorecardTitle>{selectedSite?.name}</ScorecardTitle>
                <ViewMoreInfoButton siteId={selectedSite?.id || 0} />
            </Box>
            <Button
                data-testid="side-drawer-button"
                sx={defaultButtonStyle}
                onClick={() => handleModalToggle(!sitesModalOpen)}
            >
                <span
                    style={{
                        height: '14px',
                        display: 'inline-block',
                        transition: 'transform 0.3s ease-in-out',
                        transform: sitesModalOpen ? 'rotateY(180deg)' : 'rotateY(0deg)',
                        marginRight: '8px'
                    }}
                >
                    <DoubleArrowLeft style={{ width: '14px', height: '14px' }} />
                </span>
                {sitesModalOpen ? 'Hide All Sites' : 'View All Sites'}
            </Button>
            <Drawer
                anchor="right"
                open={sitesModalOpen}
                onClose={() => handleModalToggle(false)}
                customPaperProps={{ width: '418px', maxWidth: '418px' }}
                transparentBackdrop
                variant="temporary"
                zIndex={1200}
            >
                <Box sx={{ display: "flex", flexDirection: 'column', padding: '55px 40px 20px 40px', gap: "8px", borderBottom: "1px solid #054254" }}>
                    <Label16SemiBold>Sites</Label16SemiBold>
                    <Label12Regular sx={{ fontWeight: 500 }}>
                        Select a site below to view the associated scorecard
                    </Label12Regular>
                    <DefaultSearchBar
                        searchValue={searchQuery}
                        onSearchChange={handleSearchChange}
                        placeholder="Search sites..."
                        sx={{ width: '100%', marginTop: '8px' }}
                    />
                </Box>
                <Box sx={{ height: '100vh', overflowY: 'auto' }}>
                    {filteredSites.map((site: Site) => (
                        <LazyItem key={site.id}>
                            <ListItem
                                onClick={() => onSiteButtonClick(site)}
                                disablePadding
                            >
                                <ListItemButton selected={site.id === selectedSite?.id} sx={{ padding: 0 }}>
                                    <ListItemText
                                        primary={site.name}
                                        sx={{
                                            wordBreak: 'break-word',
                                            whiteSpace: 'normal',
                                            flex: 1,
                                            minWidth: 0,
                                            padding: '20px 40px',
                                            borderBottom: "1px solid #D4D4D4",
                                            margin: '0px',
                                        }}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontWeight: site.id === selectedSite?.id ? 600 : 500,
                                                lineHeight: '24px',
                                                fontSize: '14px',
                                                fontFamily: 'Inter'
                                            }
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        </LazyItem>
                    ))}
                </Box>
            </Drawer>
        </Box>
    );
};